import { standardSchemaResolver } from "@hookform/resolvers/standard-schema"
import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import * as Sentry from "@sentry/tanstackstart-react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router"
import { ArrowRight, Check, Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { EditorHeader } from "@/components/editor-header"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PdfTimelineList } from "@/features/project-editor/components/PdfTimelineList"
import {
  getStores,
  listStoreConfigurations,
  createStoreConfigurationSimple,
  getAvailableLocales,
  createProject,
  getStorePdfTimeline,
  cloneProject,
} from "@/features/project-editor/server/projectServer"
import { getDefaultStoreConfigurationName } from "@/features/projects/core/defaultStoreConfigurationName"
import { StoreSelectionDialog } from "@/features/store-configuration/components/StoreSelectionDialog"
import { StoreSelector } from "@/features/store-configuration/components/StoreSelector"
import { listConfigurationVersionsFn } from "@/features/store-configuration/server/storeConfiguration"
import {
  StoreCreationModal,
  type CreatedStore,
} from "@/features/stores/components/StoreCreationModal"
import { localesMatch, normalizeLocale } from "@/features/stores/core/utils/normalizeLocale"
import { usePermissions } from "@/hooks/usePermissions"

// Define the schema for form validation using Zod
const newProjectSchema = z.object({
  name: z.string().min(1, "Project name is required"),
  storeId: z.string().optional(),
  storeConfigurationId: z.string().optional(),
  configVersionId: z.string().optional(),
  configLocale: z.string().optional(),
})

/**
 * Formats a locale code (e.g., "fr-LU") to a human-readable display name
 * (e.g., "Luxembourg - French")
 */
function formatLocaleDisplay(locale: string): string {
  try {
    const [language, region] = locale.split("-")
    const languageDisplay = new Intl.DisplayNames(["en"], { type: "language" })
    const regionDisplay = new Intl.DisplayNames(["en"], { type: "region" })

    const languageName = languageDisplay.of(language.toLowerCase())
    const regionName = region ? regionDisplay.of(region.toUpperCase()) : null

    if (regionName && languageName) {
      return `${regionName} - ${languageName}`
    }
    return languageName || locale
  } catch {
    return locale
  }
}

export const Route = createFileRoute("/(authenticated)/projects/new/")({
  component: NewProject,
})

type StoreOption = {
  id: string
  branchNumber?: string
  name: string
  city: string
  fullAddress: string
  countryCode?: string
  locale?: string
  initialOpeningDate?: Date
  latestConfigurationId?: string
  latestConfigurationName?: string
  latestConfigurationValidFrom?: Date
}

type NewProjectFormValues = z.infer<typeof newProjectSchema>

// Workflow step type for the multi-step flow
type WorkflowStep = "store-selection" | "pdf-timeline" | "project-details"

function NewProject() {
  // All hooks must be called unconditionally at the top
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions()
  const navigate = useNavigate({ from: Route.fullPath })
  const [serverError, setServerError] = useState<string | null>(null)
  const [showStoreDialog, setShowStoreDialog] = useState(false)
  const [showStoreCreationModal, setShowStoreCreationModal] = useState(false)
  const [selectedStore, setSelectedStore] = useState<StoreOption | null>(null)
  const [workflowStep, setWorkflowStep] = useState<WorkflowStep>("store-selection")
  const [cloningProjectId, setCloningProjectId] = useState<string | undefined>()

  // Query for stores list
  const { data: stores = [] } = useQuery<StoreOption[]>({
    queryKey: ["stores"],
    queryFn: () => getStores() as unknown as Promise<StoreOption[]>,
  })

  // Query for configuration versions (global optimization rules)
  const { data: configVersions = [] } = useQuery({
    queryKey: ["configurationVersions"],
    queryFn: () => listConfigurationVersionsFn({ data: { includeArchived: false } }),
  })

  // Query for available locales
  const { data: availableLocales = [], isLoading: isLoadingLocales } = useQuery({
    queryKey: ["availableLocales"],
    queryFn: () => getAvailableLocales({ data: undefined }),
  })

  // Query for PDF timeline when store is selected
  const pdfTimelineQuery = useQuery({
    queryKey: ["store-pdf-timeline", selectedStore?.id],
    queryFn: () =>
      selectedStore
        ? getStorePdfTimeline({ data: { storeId: selectedStore.id } })
        : Promise.resolve([]),
    enabled: Boolean(selectedStore?.id),
  })

  // Find the active configuration version
  const activeConfigVersion = configVersions.find((v) => v.isActive)

  const form = useForm<NewProjectFormValues>({
    resolver: standardSchemaResolver(newProjectSchema),
    defaultValues: {
      name: "",
      storeId: undefined,
      storeConfigurationId: undefined,
      configVersionId: undefined,
      configLocale: undefined,
    },
    mode: "onChange",
  })

  const [newConfigValidFrom, setNewConfigValidFrom] = useState(
    new Date().toISOString().slice(0, 10),
  )

  // Query for store configurations (depends on selectedStore)
  const storeConfigsQuery = useQuery({
    queryKey: ["store-configs", selectedStore?.id],
    queryFn: () =>
      selectedStore
        ? listStoreConfigurations({ data: { storeId: selectedStore.id } })
        : Promise.resolve({ configurations: [] }),
    enabled: Boolean(selectedStore?.id),
  })

  // Mutation for creating store configurations
  const createStoreConfigMutation = useMutation({
    mutationFn: (payload: { storeId: string; validFrom?: string; configurationName?: string }) =>
      createStoreConfigurationSimple({ data: payload }),
    onSuccess: async (result) => {
      form.setValue("storeConfigurationId", result.configuration.id)
      await storeConfigsQuery.refetch()
    },
  })

  const createProjectMutation = useMutation({
    mutationFn: createProject,
    onSuccess: (result) => {
      if (result.projectId) {
        void navigate({
          to: `/projects/new/${result.projectId}/upload`,
        })
      } else {
        const errorMessage = "Received an unexpected response from the server."
        setServerError(errorMessage)
        Sentry.captureMessage(errorMessage, { level: "warning" })
      }
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to submit the form."
      setServerError(errorMessage)
      Sentry.captureException(error, {
        extra: { context: "Form onSubmit Catch Block" },
      })
    },
  })

  // Mutation for cloning projects
  const cloneProjectMutation = useMutation({
    mutationFn: cloneProject,
    onSuccess: (result) => {
      if (result.projectId) {
        // Navigate to the optimize step (skip upload/crop/process since shelves geometry is cloned)
        void navigate({
          to: `/projects/edit/${result.projectId}/optimize`,
        })
      }
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to clone project."
      setServerError(errorMessage)
      Sentry.captureException(error, {
        extra: { context: "Clone Project Error" },
      })
      setCloningProjectId(undefined)
    },
  })

  // Set default config version to active when loaded
  useEffect(() => {
    if (activeConfigVersion && !form.getValues("configVersionId")) {
      form.setValue("configVersionId", activeConfigVersion.id)
    }
  }, [activeConfigVersion, form])

  // Auto-select first store configuration when available
  useEffect(() => {
    if (
      selectedStore &&
      storeConfigsQuery.data?.configurations?.length &&
      !form.getValues("storeConfigurationId")
    ) {
      form.setValue("storeConfigurationId", storeConfigsQuery.data.configurations[0].id)
    }
  }, [selectedStore, storeConfigsQuery.data, form])

  // Permission check - must be after all hooks
  const canCreate = hasPermission("project", "create").allowed

  // Show loading while permissions are being checked
  if (isLoadingPermissions) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!canCreate) {
    return <Navigate to="/projects" />
  }

  // Handle store selection
  const handleSelectStore = (storeId: string) => {
    const store = stores.find((s) => s.id === storeId)
    if (store) {
      setSelectedStore(store)
      form.setValue("storeId", storeId)
      if (!form.getValues("name")) {
        form.setValue("name", store.name)
      }
      // Default to latest configuration if present
      if (store.latestConfigurationId) {
        form.setValue("storeConfigurationId", store.latestConfigurationId)
      }
      // Default locale to store's locale. Match case-insensitively so legacy
      // mixed-casing in `availableLocales` (e.g. `nl-nl` vs `nl-NL`) doesn't
      // silently fall through; persist the canonical form on the project so
      // new rows are consistent.
      if (store.locale && availableLocales.some((l) => localesMatch(l, store.locale))) {
        form.setValue("configLocale", normalizeLocale(store.locale))
      }
      setShowStoreDialog(false)
      // Move to PDF timeline step to show previous PDFs
      setWorkflowStep("pdf-timeline")
    }
  }

  // Handle store creation callback from StoreCreationModal
  const handleStoreCreated = (store: CreatedStore) => {
    setSelectedStore({
      id: store.id,
      name: store.name,
      city: store.city,
      fullAddress: store.fullAddress,
      branchNumber: store.branchNumber ?? undefined,
      locale: store.locale,
    })
    form.setValue("storeId", store.id)
    form.setValue("storeConfigurationId", undefined)
    // A just-created store's locale may not be in the cached
    // `availableLocales` list yet (we invalidate, but the query is in flight);
    // trust the canonical locale we got back from the server.
    if (store.locale) {
      form.setValue("configLocale", normalizeLocale(store.locale))
    }
    setShowStoreCreationModal(false)
    // New store created - go directly to project details (no previous PDFs)
    setWorkflowStep("project-details")
  }

  // Handle cloning a project
  const handleCloneProject = (projectId: string, projectName: string) => {
    setCloningProjectId(projectId)
    const newName = `${form.getValues("name") || projectName} (Copy)`
    cloneProjectMutation.mutate({
      data: {
        sourceProjectId: projectId,
        newName,
        configVersionId: form.getValues("configVersionId"),
        configLocale: form.getValues("configLocale"),
      },
    })
  }

  // Handle "Upload new PDF" from timeline
  const handleUploadNew = () => {
    setWorkflowStep("project-details")
  }

  // Handle going back to store selection
  const handleBackToStoreSelection = () => {
    setWorkflowStep("store-selection")
    setSelectedStore(null)
    form.reset()
  }

  const onSubmit = async (values: NewProjectFormValues) => {
    setServerError(null)
    try {
      const result = await createProject({ data: values })
      if (result.projectId) {
        await navigate({
          to: `/projects/new/${result.projectId}/upload`,
        })
      } else {
        const errorMessage = "Received an unexpected response from the server."
        setServerError(errorMessage)
        Sentry.captureMessage(errorMessage, { level: "warning" })
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to submit the form."
      setServerError(errorMessage)
      Sentry.captureException(error, {
        extra: { context: "Form onSubmit Catch Block" },
      })
    }
  }

  const { isSubmitting } = form.formState
  const isSubmittingMutation = createProjectMutation.isPending

  // Determine current step label for the header
  const getCurrentStepLabel = () => {
    switch (workflowStep) {
      case "store-selection":
        return t`Select Store`
      case "pdf-timeline":
        return t`Previous Blueprints`
      case "project-details":
        return t`Project Details`
      default:
        return t`Details`
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Editor Header */}
      <EditorHeader
        contextLabel={t`New Blueprint`}
        projectName={getCurrentStepLabel()}
        steps={[
          {
            id: "details",
            label: t`Details`,
            to: "/projects/new",
          },
          {
            id: "upload",
            label: t`Upload`,
            to: "/projects/new",
            disabled: true, // Can't skip ahead
          },
          {
            id: "crop",
            label: t`Crop Layout`,
            to: "/projects/new",
            disabled: true, // Can't skip ahead
          },
          {
            id: "process",
            label: t`Create Fixtures`,
            to: "/projects/new",
            disabled: true,
          },
          {
            id: "optimize",
            label: t`Design Blueprint`,
            to: "/projects/new",
            disabled: true,
          },
        ]}
        currentStepId="details"
      />

      <div className="container flex min-h-0 flex-grow flex-col py-6">
        {serverError && (
          <div
            className="relative mb-4 shrink-0 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700"
            role="alert"
          >
            <strong className="font-bold">Error:</strong>
            <span className="block sm:inline"> {serverError}</span>
          </div>
        )}

        {/* Step 1: Store Selection */}
        {workflowStep === "store-selection" && (
          <div className="mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col gap-6">
            <div className="shrink-0">
              <h2 className="text-2xl font-semibold">
                <Trans>Select a Store</Trans>
              </h2>
              <p className="text-muted-foreground mt-2">
                <Trans>
                  Search and pick the store this project is for. Store information is shared across
                  projects.
                </Trans>
              </p>
            </div>

            <StoreSelector
              stores={stores}
              onSelectStore={handleSelectStore}
              className="flex min-h-0 flex-1 flex-col gap-4"
              listClassName="flex-1 min-h-0 space-y-2 overflow-y-auto"
            />

            <StoreCreationModal
              showTrigger={false}
              open={showStoreCreationModal}
              onOpenChange={setShowStoreCreationModal}
              onStoreCreated={handleStoreCreated}
            />
            {hasPermission("store", "create").allowed && (
              <div className="shrink-0 border-t pt-4">
                <p className="text-muted-foreground mb-2 text-sm">
                  <Trans>Can&apos;t find the store?</Trans>
                </p>
                <Button
                  variant="outline"
                  onClick={() => setShowStoreCreationModal(true)}
                  className="w-full justify-center"
                >
                  <Trans>Create New Store</Trans>
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: PDF Timeline (only shown when store has previous PDFs) */}
        {workflowStep === "pdf-timeline" && selectedStore && (
          <div className="mx-auto w-full max-w-2xl flex-1 space-y-6 overflow-y-auto">
            {/* Store info header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">{selectedStore.name}</h2>
                <p className="text-muted-foreground text-sm">
                  {selectedStore.city}
                  {selectedStore.fullAddress && ` - ${selectedStore.fullAddress}`}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleBackToStoreSelection}>
                <Trans>Change Store</Trans>
              </Button>
            </div>

            {/* Loading state */}
            {pdfTimelineQuery.isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* PDF Timeline */}
            {!pdfTimelineQuery.isLoading && (
              <PdfTimelineList
                entries={pdfTimelineQuery.data ?? []}
                onCloneProject={handleCloneProject}
                onUploadNew={handleUploadNew}
                isCloning={cloneProjectMutation.isPending}
                selectedProjectId={cloningProjectId}
              />
            )}
          </div>
        )}

        {/* Step 3: Project Details Form */}
        {workflowStep === "project-details" && (
          <div className="mx-auto w-full max-w-xl flex-1 overflow-y-auto">
            {/* Store info header when coming from timeline */}
            {selectedStore && (
              <div className="mb-6 flex items-center justify-between rounded-lg border bg-muted/30 p-4">
                <div>
                  <h3 className="font-medium">{selectedStore.name}</h3>
                  <p className="text-muted-foreground text-sm">
                    {selectedStore.city}
                    {selectedStore.fullAddress && ` - ${selectedStore.fullAddress}`}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={handleBackToStoreSelection}>
                  <Trans>Change</Trans>
                </Button>
              </div>
            )}

            <Form {...form}>
              <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-4">
                {/* Store field (only show if no store selected yet) */}
                {!selectedStore && (
                  <FormField
                    control={form.control}
                    name="storeId"
                    render={() => (
                      <FormItem>
                        <FormLabel className="text-lg">Store</FormLabel>
                        <FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowStoreDialog(true)}
                            className="w-full justify-start"
                          >
                            <Trans>Select a store...</Trans>
                          </Button>
                        </FormControl>
                        <FormDescription>
                          Choose the store location for this project (optional).
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-lg">Project Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Name your project" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {selectedStore && (
                  <FormField
                    control={form.control}
                    name="storeConfigurationId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-lg">Store Configuration</FormLabel>
                        <FormControl>
                          <div className="flex flex-col gap-2">
                            <Select
                              value={field.value}
                              onValueChange={(val) => field.onChange(val)}
                            >
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={
                                    storeConfigsQuery.isLoading
                                      ? "Loading configurations..."
                                      : "Select configuration..."
                                  }
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {(storeConfigsQuery.data?.configurations ?? []).map((config) => (
                                  <SelectItem key={config.id} value={config.id}>
                                    {config.name} (
                                    {new Intl.DateTimeFormat("en", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    }).format(new Date(config.validFrom))}
                                    )
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <div className="flex items-center gap-2">
                              <Input
                                type="date"
                                value={newConfigValidFrom}
                                onChange={(e) => setNewConfigValidFrom(e.target.value)}
                                className="max-w-xs"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  createStoreConfigMutation.mutate({
                                    storeId: selectedStore.id,
                                    validFrom: newConfigValidFrom,
                                    configurationName: getDefaultStoreConfigurationName(
                                      selectedStore.name,
                                    ),
                                  })
                                }
                                disabled={
                                  createStoreConfigMutation.isPending || !newConfigValidFrom
                                }
                              >
                                {createStoreConfigMutation.isPending
                                  ? "Creating..."
                                  : "Create new configuration"}
                              </Button>
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Choose an existing configuration or create a new one for this store.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Locale Context */}
                <FormField
                  control={form.control}
                  name="configLocale"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-lg">
                        <Trans>Locale Context</Trans>
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value ?? "none"}
                          onValueChange={(val) => field.onChange(val === "none" ? undefined : val)}
                          disabled={isLoadingLocales || !form.watch("configVersionId")}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                isLoadingLocales
                                  ? t`Loading locales...`
                                  : !form.watch("configVersionId")
                                    ? t`Select a version first`
                                    : t`Select a locale (optional)`
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              <span className="italic text-neutral-500">
                                <Trans>No locale (use base values)</Trans>
                              </span>
                            </SelectItem>
                            {[...availableLocales]
                              .sort((a, b) =>
                                formatLocaleDisplay(a).localeCompare(formatLocaleDisplay(b)),
                              )
                              .map((locale: string) => (
                                <SelectItem key={locale} value={locale}>
                                  <div className="flex items-center gap-2">
                                    <span>{formatLocaleDisplay(locale)}</span>
                                    {locale === field.value && (
                                      <Check className="ml-auto h-4 w-4 text-green-600" />
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormDescription>
                        <Trans>
                          Locale selection applies regional overrides from the configuration.
                          Defaults to the store's location if available.
                        </Trans>
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  disabled={isSubmitting || isSubmittingMutation}
                  aria-busy={isSubmitting || isSubmittingMutation}
                  className="bg-green-600 text-white hover:bg-green-700"
                >
                  {isSubmitting || isSubmittingMutation ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="mr-2 h-4 w-4" /> <Trans>Continue to Upload</Trans>
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </div>
        )}

        {/* Store Selection Dialog */}
        <StoreSelectionDialog
          open={showStoreDialog}
          onOpenChange={setShowStoreDialog}
          onSelectStore={handleSelectStore}
          existingStores={stores}
        />
      </div>
    </div>
  )
}
